"use client";

import { CheckCircle2 } from "lucide-react";

type ProcessStep = {
  title: string;
  description: string;
  duration?: string;
  details: string[];
};

const processSteps: ProcessStep[] = [
  {
    title: "Consultation",
    description: "We start by understanding your vision, style preferences, and goals.",
    duration: "30-60 min",
    details: [
      "Discuss your vision and expectations",
      "Review style preferences and mood boards",
      "Understand timeline and deliverables",
      "Answer any questions you have",
    ],
  },
  {
    title: "Planning",
    description: "We plan every detail to ensure a smooth and successful shoot.",
    duration: "1-2 weeks",
    details: [
      "Location scouting and selection",
      "Shot list and timeline creation",
      "Equipment and team preparation",
      "Final confirmation and logistics",
    ],
  },
  {
    title: "Shoot",
    description: "On the day, we capture your moments with professionalism and creativity.",
    duration: "Varies by project",
    details: [
      "Professional equipment and backup gear",
      "Multiple angles and compositions",
      "Natural and artificial lighting expertise",
      "Documentary-style coverage",
    ],
  },
  {
    title: "Editing",
    description: "We carefully curate and edit your images to match our signature style.",
    duration: "2-4 weeks",
    details: [
      "Image selection and curation",
      "Color grading and retouching",
      "Cinematic color tones",
      "Fine-art finish",
    ],
  },
  {
    title: "Delivery",
    description: "You receive your final images in a beautiful, organized format.",
    duration: "Within timeline",
    details: [
      "High-resolution digital files",
      "Online gallery access",
      "Print-ready formats",
      "Usage rights and licensing",
    ],
  },
];

export default function Process() {
  return (
    <section
      id="process"
      className="relative isolate mx-auto w-full px-4 py-12 sm:px-6 sm:py-16 md:py-20 lg:px-8 lg:py-24"
    >
      {/* Fancy gradient background - continues from about bottom left and bottom right */}
      <div className="pointer-events-none absolute inset-0 -z-10 overflow-hidden">
        {/* Base gradient - starts with about's ending colors at top left and top right */}
        <div
          className="absolute inset-0"
          style={{
            background: `
              radial-gradient(ellipse 100% 80% at top left, oklch(0.6 0.118 184.704 / 0.08), transparent 60%),
              radial-gradient(ellipse 100% 80% at top center, oklch(0.769 0.188 70.08 / 0.1), transparent 60%),
              radial-gradient(ellipse 100% 80% at top right, oklch(0.769 0.188 70.08 / 0.1), transparent 60%),
              radial-gradient(ellipse 100% 80% at left center, oklch(0.646 0.222 41.116 / 0.15), transparent 60%),
              radial-gradient(ellipse 100% 80% at right center, oklch(0.828 0.189 84.429 / 0.12), transparent 60%),
              radial-gradient(ellipse 100% 80% at bottom left, oklch(0.6 0.118 184.704 / 0.08), transparent 60%),
              radial-gradient(ellipse 100% 80% at bottom center, oklch(0.828 0.189 84.429 / 0.12), transparent 60%),
              radial-gradient(ellipse 100% 80% at bottom right, oklch(0.828 0.189 84.429 / 0.12), transparent 60%),
              linear-gradient(to bottom left, oklch(0.769 0.188 70.08 / 0.1) 0%, transparent 40%, oklch(0.6 0.118 184.704 / 0.08) 100%),
              oklch(1 0 0)
            `,
          }}
        />

        {/* Animated floating orbs */}
        <div className="bg-chart-1/20 absolute top-20 -left-32 size-96 animate-pulse rounded-full blur-3xl motion-safe:animate-pulse" />
        <div className="bg-chart-4/20 absolute -right-32 bottom-20 size-80 animate-pulse rounded-full blur-3xl motion-safe:animate-pulse motion-safe:delay-1000" />
        <div className="bg-chart-2/15 absolute top-1/2 left-1/2 size-72 -translate-x-1/2 -translate-y-1/2 animate-pulse rounded-full blur-3xl motion-safe:animate-pulse motion-safe:delay-2000" />

        {/* Subtle mesh gradient overlay */}
        <div
          className="absolute inset-0 opacity-30"
          style={{
            background:
              "linear-gradient(to bottom left, transparent 0%, oklch(0.828 0.189 84.429 / 0.05) 30%, oklch(0.646 0.222 41.116 / 0.05) 70%, transparent 100%)",
          }}
        />
      </div>

      <div className="container mx-auto">
        {/* Header */}
        <div className="motion-safe:animate-in motion-safe:fade-in-0 motion-safe:slide-in-from-bottom-2 mb-12 text-center motion-safe:duration-700 sm:mb-16">
          <span className="text-primary text-xs font-medium tracking-wider uppercase sm:text-sm md:text-base">
            How We Work
          </span>
          <h2 className="mt-2 text-2xl font-semibold tracking-tight sm:text-3xl md:text-4xl lg:text-5xl">
            Our Process
          </h2>
          <p className="text-muted-foreground mx-auto mt-4 max-w-2xl text-sm leading-relaxed sm:text-base md:text-lg">
            From initial consultation to final delivery, we guide you through every step with
            professionalism and attention to detail.
          </p>
        </div>

        {/* Process Steps */}
        <div className="relative">
          {/* Connection line (desktop only) */}
          <div className="via-primary/20 absolute top-0 left-1/2 hidden h-full w-0.5 -translate-x-0.5 bg-linear-to-b from-transparent to-transparent md:block" />

          <div className="space-y-8 sm:space-y-12 md:space-y-16">
            {processSteps.map((step, idx) => {
              const isEven = idx % 2 === 0;
              return (
                <div
                  key={step.title}
                  className={`group motion-safe:animate-in motion-safe:fade-in-0 motion-safe:slide-in-from-bottom-2 relative flex flex-col gap-4 motion-safe:duration-700 md:flex-row md:items-center md:gap-8 lg:gap-12 ${
                    isEven ? "md:flex-row" : "md:flex-row-reverse"
                  }`}
                  style={{
                    animationDelay: `${idx * 75}ms`,
                  }}
                >
                  {/* Step Number & Icon */}
                  <div className="relative z-10 flex shrink-0 items-center justify-center md:w-20 lg:w-40">
                    {/* Step number circle */}
                    <div
                      className={`bg-primary/10 text-primary border-primary/30 group-hover:border-primary relative z-20 flex size-16 items-center justify-center rounded-full border-2 text-base font-bold shadow-lg transition-all duration-300 group-hover:scale-110 group-hover:shadow-xl sm:size-20 md:size-24 lg:size-28 lg:text-2xl`}
                    >
                      {idx + 1}
                    </div>
                  </div>

                  {/* Step Content */}
                  <div
                    className={`bg-background/80 supports-backdrop-filter:bg-background/60 border-border/50 group-hover:border-border relative flex-1 rounded-xl border p-6 shadow-xs backdrop-blur-sm transition-all duration-300 group-hover:-translate-y-1 group-hover:shadow-md sm:p-8 lg:p-10 ${
                      isEven ? "md:ml-0" : "md:mr-0"
                    }`}
                  >
                    {/* Duration badge */}
                    {step.duration && (
                      <span className="text-primary bg-primary/10 mb-3 inline-block rounded-full px-3 py-1 text-xs font-medium sm:mb-4 sm:text-sm">
                        {step.duration}
                      </span>
                    )}

                    {/* Title */}
                    <h3 className="group-hover:text-primary mb-2 text-xl font-semibold transition-colors duration-300 sm:text-2xl md:text-3xl">
                      {step.title}
                    </h3>

                    {/* Description */}
                    <p className="text-muted-foreground mb-4 text-sm leading-relaxed sm:mb-6 sm:text-base md:text-lg">
                      {step.description}
                    </p>

                    {/* Details list */}
                    <ul className="space-y-2 sm:space-y-3">
                      {step.details.map((detail, detailIdx) => (
                        <li
                          key={detailIdx}
                          className="text-muted-foreground flex items-start gap-3 text-sm sm:text-base"
                        >
                          <CheckCircle2 className="text-primary mt-0.5 size-5 shrink-0" />
                          <span className="flex-1">{detail}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* CTA */}
        <div
          className="motion-safe:animate-in motion-safe:fade-in-0 motion-safe:slide-in-from-bottom-2 mt-12 text-center motion-safe:duration-700 sm:mt-16"
          style={{
            animationDelay: "500ms",
          }}
        >
          <p className="text-muted-foreground mb-4 text-sm sm:text-base">
            Ready to start your project? Let&apos;s discuss your vision.
          </p>
          <a
            href="#contact"
            className="focus-visible:ring-ring bg-primary text-primary-foreground hover:bg-primary/90 inline-block rounded-md px-6 py-3 text-sm font-medium transition-all duration-300 hover:scale-105 hover:shadow-lg focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none sm:px-8 sm:py-3.5 sm:text-base"
          >
            Get Started
          </a>
        </div>
      </div>
    </section>
  );
}
